import { create } from 'zustand';
import {
  cancelSimulationRun,
  createSimulationRun,
  getSimulationResults,
  getSimulationStatus,
  simulationStreamUrl,
  updateScenarios,
} from '../api/client';
import type {
  GateOverrideEntry,
  ScenarioOverrides,
  ScenarioPreset,
  SimulationRunState,
  SimulationSummary,
} from '../types/scenario';
import type { ValidationIssue } from '../types/validation';
import type { SimulationDurationMode, SimulationRunMode } from '../lib/simulationDuration';
import {
  DEFAULT_VISUAL_FPS,
  MAX_VISUAL_FPS,
  MIN_VISUAL_FPS,
  MAX_DURATION_MINUTES,
  visualFpsToIntervalMs,
} from '../lib/simulationDuration';
import {
  pushLiveFrame,
  resetLiveFrameState,
  mergeVisualCatalog,
  setTargetVisualFps,
  setVisualCatalog,
  getVisualFpsDiagnostics,
  getDroppedSnapshotCount,
  type LiveFrame,
  type ProductVisualStyle,
} from '../lib/liveFrameAnimation';
import { collectVisualCatalogFromDocument } from '../lib/productVisualCatalog';
import { useEditorStore } from './editorStore';
import {
  fetchUserPreferences,
  updateUserPreferences,
} from '../api/client';
import {
  loadLocalRunPreferences,
  mergeProjectRunConfig,
  parseSimulationRunPreferences,
  resolveProjectRunConfig,
  saveLocalRunPreferences,
  type ProjectRunConfig,
} from '../types/simulationRunPreferences';
import {
  finalizeGateOverrides,
  GATE_PENDING_TIMEOUT_MS,
  isGateToggleDeliverable,
  reconcileGateOverrides,
  stableGateUiState,
  transitionalGateUiState,
} from '../lib/gateOverrideHelpers';

// ------------------------------------------------------------
// Simulation / scenario runtime state (Zustand)
// ------------------------------------------------------------

interface StreamStepMessage {
  type: 'step';
  sim_time: number;
  progress_pct: number;
  metrics: Record<string, unknown>;
  gate_states: Record<string, string>;
  new_warnings?: SimulationSummary['bottleneck_warnings'];
}

interface StreamCompletedMessage {
  type: 'completed';
  progress_pct: number;
  summary: Record<string, unknown>;
}

interface StreamStatusMessage {
  type: 'status';
  status: string;
  progress_pct: number;
}

interface StreamErrorMessage {
  type: 'error';
  message: string;
}

interface StreamCancelledMessage {
  type: 'cancelled';
  progress_pct: number;
  summary: Record<string, unknown>;
}

interface StreamReplayMessage {
  type: 'replay';
  progress_pct: number;
  steps: Array<{
    sim_time: number;
    progress_pct: number;
    metrics: Record<string, unknown>;
    gate_states: Record<string, string>;
    live_frame?: LiveFrame;
  }>;
}

interface StreamLiveFrameMessage {
  type: 'live_frame';
  frame: LiveFrame;
  progress_pct: number;
  gate_states: Record<string, string>;
}

interface StreamVisualCatalogMessage {
  type: 'visual_catalog';
  styles: ProductVisualStyle[];
}

interface StreamGateAckMessage {
  type: 'gate_ack';
  command_id: string;
  gate_id: string;
  state: string;
  applied: boolean;
  applied_sim_time?: number;
}

type StreamMessage =
  | StreamStepMessage
  | StreamLiveFrameMessage
  | StreamVisualCatalogMessage
  | StreamGateAckMessage
  | StreamCompletedMessage
  | StreamStatusMessage
  | StreamErrorMessage
  | StreamCancelledMessage
  | StreamReplayMessage;

interface SimulationState {
  bottomPanelOpen: boolean;
  bottomPanelTab: 'scenario' | 'gates' | 'results' | 'diagnostics';
  activeScenarioId: string | null;
  liveOverrides: ScenarioOverrides;
  gateOverrides: Record<string, GateOverrideEntry>;
  scenarios: ScenarioPreset[];
  run: SimulationRunState;
  simulationDurationMode: SimulationDurationMode;
  simulationRunMode: SimulationRunMode;
  durationMinutes: number;
  visualFps: number;
  receivedVisualFps: number;
  renderedVisualFps: number;
  droppedSnapshotCount: number;
  runPreferencesHydrated: boolean;
  lastSubmittedRunConfig: ProjectRunConfig | null;
  wsRef: WebSocket | null;
  showProductSprites: boolean;
  /** Wall-clock pacing multiplier for live runs (1, 2, or 5). */
  simulationTimeScale: number;
  /** Gate row hovered in the Gates panel — highlights matching node on canvas. */
  highlightedGateId: string | null;

  hydrateRunPreferences: (projectId: string) => Promise<void>;
  applyProjectRunConfig: (config: ProjectRunConfig) => void;
  persistRunPreferences: (projectId: string, config: ProjectRunConfig) => Promise<void>;

  setBottomPanelOpen: (open: boolean) => void;
  setShowProductSprites: (show: boolean) => void;
  setBottomPanelTab: (tab: 'scenario' | 'gates' | 'results' | 'diagnostics') => void;
  setScenarios: (scenarios: ScenarioPreset[]) => void;
  setActiveScenarioId: (id: string | null) => void;
  setGlobalRateMultiplier: (value: number) => void;
  setInputRateMultiplier: (nodeId: string, value: number | undefined) => void;
  setProcessorCapacity: (nodeId: string, capacity: number | undefined) => void;
  setBellCurveAdjustments: (
    nodeId: string,
    patch: { peak_time_shift_seconds?: number; spread_multiplier?: number },
  ) => void;
  toggleGate: (nodeId: string, state: 'open' | 'closed') => void;
  saveScenarioPreset: (preset: ScenarioPreset) => void;
  deleteScenarioPreset: (id: string) => void;
  loadScenarioPreset: (preset: ScenarioPreset) => void;
  setSimulationDurationMode: (mode: SimulationDurationMode) => void;
  setSimulationRunMode: (mode: SimulationRunMode) => void;
  setDurationMinutes: (minutes: number) => void;
  setVisualFps: (fps: number) => void;
  setSimulationTimeScale: (scale: number) => void;
  setHighlightedGateId: (gateId: string | null) => void;
  persistScenarios: (projectId: string) => Promise<void>;
  runSimulation: (projectId: string) => Promise<void>;
  cancelSimulation: () => Promise<void>;
  resetRun: () => void;
  setRunValidationIssues: (issues: ValidationIssue[], error?: string | null) => void;
  reset: () => void;
}

function newId(): string {
  return crypto.randomUUID();
}

function defaultScenarios(): ScenarioPreset[] {
  return [
    {
      id: newId(),
      name: 'Normal day',
      description: 'Baseline operating conditions',
      overrides: { global_rate_multiplier: 1.0 },
    },
    {
      id: newId(),
      name: 'Heavy flock',
      description: 'Elevated incoming flow (120%)',
      overrides: { global_rate_multiplier: 1.2 },
    },
  ];
}

const initialRun: SimulationRunState = {
  runId: null,
  status: 'idle',
  error: null,
  issues: [],
  summary: null,
  timeseries: [],
  progressPct: 0,
  liveGateStates: {},
  liveOverlay: null,
  liveSimTime: 0,
  visualPlaybackActive: false,
  runUntilStopped: false,
  runMode: null,
  wsConnectionState: 'disconnected',
  visualizedProductCount: null,
  totalProductCount: null,
};

let activeSimulationRunMode: SimulationRunMode = 'realtime';

const gatePendingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

function clearGatePendingTimeout(gateId: string) {
  const timeoutId = gatePendingTimeouts.get(gateId);
  if (timeoutId != null) {
    clearTimeout(timeoutId);
    gatePendingTimeouts.delete(gateId);
  }
}

function clearAllGatePendingTimeouts() {
  for (const timeoutId of gatePendingTimeouts.values()) {
    clearTimeout(timeoutId);
  }
  gatePendingTimeouts.clear();
}

function scheduleGatePendingTimeout(
  gateId: string,
  set: (partial: Partial<SimulationState> | ((state: SimulationState) => Partial<SimulationState>)) => void,
) {
  clearGatePendingTimeout(gateId);
  const timeoutId = setTimeout(() => {
    gatePendingTimeouts.delete(gateId);
    set((s) => {
      const entry = s.gateOverrides[gateId];
      if (!entry?.pending_command_id) {
        return {};
      }
      const serverState = s.run.liveGateStates[gateId];
      const finalState = serverState ?? entry.state;
      return {
        gateOverrides: {
          ...s.gateOverrides,
          [gateId]: {
            ...entry,
            state: finalState,
            ui_state: stableGateUiState(finalState),
            pending_command_id: undefined,
          },
        },
      };
    });
  }, GATE_PENDING_TIMEOUT_MS);
  gatePendingTimeouts.set(gateId, timeoutId);
}

function stopVisualDrain() {
  lastOverlayUpdateAt = 0;
  resetLiveFrameState();
}

function mergeLiveGateStates(
  current: Record<string, 'open' | 'closed'>,
  server: Record<string, string>,
  gateOverrides: Record<string, GateOverrideEntry>,
): Record<string, 'open' | 'closed'> {
  const merged = { ...current };
  for (const [gateId, state] of Object.entries(server)) {
    const entry = gateOverrides[gateId];
    if (entry?.pending_command_id) {
      merged[gateId] = entry.state;
      continue;
    }
    merged[gateId] = state as 'open' | 'closed';
  }
  return merged;
}

function applyLiveFrame(
  run: SimulationRunState,
  frame: LiveFrame,
  progressPct: number,
  gateStates: Record<string, string>,
  gateOverrides: Record<string, GateOverrideEntry>,
): Partial<SimulationRunState> {
  const liveGateStates = mergeLiveGateStates(
    run.liveGateStates,
    gateStates,
    gateOverrides,
  );

  return {
    progressPct,
    liveSimTime: frame.sim_time,
    liveGateStates,
    liveOverlay: overlayFromLiveFrame(frame),
    visualPlaybackActive: true,
    visualizedProductCount:
      frame.visualized_product_count ?? frame.products.length,
    totalProductCount: frame.total_product_count ?? frame.products.length,
  };
}

let lastOverlayUpdateAt = 0;
const OPERATIONAL_OVERLAY_INTERVAL_MS = 250;

function snapshotBlendMs(visualFps: number): number {
  return visualFpsToIntervalMs(visualFps);
}

function enqueueLiveFrame(
  msg: StreamLiveFrameMessage,
  visualFps: number,
  set: (partial: Partial<SimulationState> | ((state: SimulationState) => Partial<SimulationState>)) => void,
  get: () => SimulationState,
) {
  if (activeSimulationRunMode !== 'realtime') return;
  setTargetVisualFps(visualFps);
  pushLiveFrame(msg.frame, snapshotBlendMs(visualFps));

  const fpsDiagnostics = getVisualFpsDiagnostics();
  const droppedSnapshotCount = getDroppedSnapshotCount();
  const now = performance.now();
  const shouldUpdateOverlay =
    now - lastOverlayUpdateAt >= OPERATIONAL_OVERLAY_INTERVAL_MS;

  if (shouldUpdateOverlay) {
    lastOverlayUpdateAt = now;
    set((s) => {
      const gateOverrides = reconcileGateOverrides(
        s.gateOverrides,
        msg.gate_states,
      );
      for (const [gateId, entry] of Object.entries(gateOverrides)) {
        const prev = s.gateOverrides[gateId];
        if (prev?.pending_command_id && !entry.pending_command_id) {
          clearGatePendingTimeout(gateId);
        }
      }
      return {
        gateOverrides,
        run: {
          ...s.run,
          ...applyLiveFrame(
            s.run,
            msg.frame,
            msg.progress_pct,
            msg.gate_states,
            gateOverrides,
          ),
          visualPlaybackActive: true,
        },
        receivedVisualFps: fpsDiagnostics.receivedFps,
        renderedVisualFps: fpsDiagnostics.renderedFps,
        droppedSnapshotCount,
      };
    });
    const { run } = get();
    maybePollRunCompletionAt100(
      run.runId,
      msg.progress_pct,
      run.runUntilStopped,
      run.status,
      set,
      get,
    );
    return;
  }

  set((s) => ({
    run: {
      ...s.run,
      progressPct: msg.progress_pct,
      visualizedProductCount:
        msg.frame.visualized_product_count ?? msg.frame.products.length,
      totalProductCount:
        msg.frame.total_product_count ?? msg.frame.products.length,
      visualPlaybackActive: true,
    },
    receivedVisualFps: fpsDiagnostics.receivedFps,
    renderedVisualFps: fpsDiagnostics.renderedFps,
    droppedSnapshotCount,
  }));
  const { run } = get();
  maybePollRunCompletionAt100(
    run.runId,
    msg.progress_pct,
    run.runUntilStopped,
    run.status,
    set,
    get,
  );
}

function overlayFromLiveFrame(frame: LiveFrame) {
  return {
    routeOccupancy: frame.route_occupancy,
    processorQueues: frame.processor_queue_lengths,
    processorFlash: frame.processor_flash,
    outputFlash: frame.output_flash,
    inSystemCount: frame.in_system_count,
    outputTotals: frame.output_totals,
    outputArrivalLog: [] as Array<{
      sim_time: number;
      output_id: string;
      product_type_id: string;
      visual?: { color?: string; shape?: string; size_px?: number };
    }>,
    totalOutput: frame.total_output,
    totalGenerated: frame.total_generated,
    updatedAt: Date.now(),
  };
}

function overlayFromMetrics(metrics: Record<string, unknown>) {
  return {
    routeOccupancy: (metrics.route_occupancy ?? {}) as Record<string, number>,
    processorQueues: (metrics.processor_queue_lengths ?? {}) as Record<
      string,
      number
    >,
    processorFlash: (metrics.processor_flash ?? {}) as Record<string, number>,
    outputFlash: (metrics.output_flash ?? {}) as Record<string, number>,
    inSystemCount: Number(metrics.in_system_count ?? 0),
    outputTotals: (metrics.output_totals ?? {}) as Record<string, number>,
    outputArrivalLog: (metrics.output_arrival_log ?? []) as Array<{
      sim_time: number;
      output_id: string;
      product_type_id: string;
      visual?: { color?: string; shape?: string; size_px?: number };
    }>,
    totalOutput: Number(metrics.total_output ?? 0),
    totalGenerated: Number(metrics.total_generated ?? 0),
    updatedAt: Date.now(),
  };
}

function applyStreamStepLive(
  run: SimulationRunState,
  msg: StreamStepMessage,
): Partial<SimulationRunState> {
  const liveGateStates = { ...run.liveGateStates };
  for (const [gateId, state] of Object.entries(msg.gate_states)) {
    liveGateStates[gateId] = state as 'open' | 'closed';
  }

  return {
    progressPct: msg.progress_pct,
    liveSimTime: msg.sim_time,
    liveGateStates,
    liveOverlay: run.liveOverlay ?? overlayFromMetrics(msg.metrics),
    visualPlaybackActive: true,
  };
}

const completionPollRunIds = new Set<string>();

function applyRunFinished(
  set: (partial: Partial<SimulationState> | ((state: SimulationState) => Partial<SimulationState>)) => void,
  runId: string,
  status: 'completed' | 'cancelled' | 'error',
  results: Awaited<ReturnType<typeof getSimulationResults>> | null,
  runUntilStopped: boolean,
  progressPct = 100,
  error: string | null = null,
) {
  stopVisualDrain();
  clearAllGatePendingTimeouts();
  completionPollRunIds.delete(runId);
  set((s) => ({
    wsRef: null,
    gateOverrides: finalizeGateOverrides(s.gateOverrides),
    run: {
      runId,
      status,
      error,
      issues: [],
      summary: (results?.summary as Record<string, unknown>) ?? null,
      timeseries: (results?.timeseries as SimulationRunState['timeseries']) ?? [],
      progressPct: status === 'error' ? 0 : progressPct,
      liveGateStates: {},
      liveOverlay: null,
      liveSimTime: 0,
      visualPlaybackActive: false,
      runUntilStopped,
      runMode: activeSimulationRunMode,
      wsConnectionState: 'disconnected',
      visualizedProductCount: null,
      totalProductCount: null,
    },
  }));
}

function pollRunCompletion(
  runId: string,
  runUntilStopped: boolean,
  set: (partial: Partial<SimulationState> | ((state: SimulationState) => Partial<SimulationState>)) => void,
  get: () => SimulationState,
) {
  if (completionPollRunIds.has(runId)) {
    return;
  }
  completionPollRunIds.add(runId);

  const poll = async (attempt = 0) => {
    const run = get().run;
    if (run.runId !== runId || run.status !== 'running') {
      completionPollRunIds.delete(runId);
      return;
    }

    try {
      const status = await getSimulationStatus(runId);
      if (!status.finished) {
        if (attempt < 120) {
          setTimeout(() => void poll(attempt + 1), 500);
        } else {
          completionPollRunIds.delete(runId);
        }
        return;
      }

      const results = await getSimulationResults(runId);
      applyRunFinished(
        set,
        runId,
        status.status === 'cancelled' ? 'cancelled' : 'completed',
        results,
        runUntilStopped,
        status.progress_pct,
      );
    } catch {
      if (attempt < 120) {
        setTimeout(() => void poll(attempt + 1), 1000);
      } else {
        completionPollRunIds.delete(runId);
      }
    }
  };

  void poll();
}

function maybePollRunCompletionAt100(
  runId: string | null,
  progressPct: number,
  runUntilStopped: boolean,
  status: string,
  set: (partial: Partial<SimulationState> | ((state: SimulationState) => Partial<SimulationState>)) => void,
  get: () => SimulationState,
) {
  if (
    runId &&
    status === 'running' &&
    !runUntilStopped &&
    progressPct >= 100
  ) {
    pollRunCompletion(runId, runUntilStopped, set, get);
  }
}

function finishRunFromStreamMessage(
  runId: string,
  status: 'completed' | 'cancelled',
  summary: Record<string, unknown>,
  progressPct: number,
  runUntilStopped: boolean,
  set: (partial: Partial<SimulationState> | ((state: SimulationState) => Partial<SimulationState>)) => void,
  get: () => SimulationState,
) {
  applyRunFinished(
    set,
    runId,
    status,
    {
      run_id: runId,
      status,
      summary,
      timeseries: [],
    },
    runUntilStopped,
    progressPct,
  );

  void getSimulationResults(runId)
    .then((results) => {
      set((s) => {
        if (s.run.runId !== runId || s.run.status !== status) {
          return {};
        }
        return {
          run: {
            ...s.run,
            summary: results.summary as Record<string, unknown>,
            timeseries: results.timeseries as SimulationRunState['timeseries'],
          },
        };
      });
    })
    .catch(() => {
      const run = get().run;
      if (run.runId === runId && run.status === 'running') {
        pollRunCompletion(runId, runUntilStopped, set, get);
      }
    });
}

function pollCalculateRun(
  runId: string,
  runUntilStopped: boolean,
  set: (partial: Partial<SimulationState> | ((state: SimulationState) => Partial<SimulationState>)) => void,
  get: () => SimulationState,
) {
  const poll = async () => {
    if (get().run.status !== 'running' || get().run.runId !== runId) return;
    try {
      const status = await getSimulationStatus(runId);
      set((s) => ({
        run: { ...s.run, progressPct: status.progress_pct },
      }));
      if (!status.finished) {
        setTimeout(() => void poll(), 1000);
        return;
      }
      const results = await getSimulationResults(runId);
      const finalStatus =
        status.status === 'cancelled'
          ? 'cancelled'
          : status.status === 'failed'
            ? 'error'
            : 'completed';
      applyRunFinished(
        set,
        runId,
        finalStatus,
        results,
        runUntilStopped,
        status.progress_pct,
        finalStatus === 'error' ? 'Simulation failed' : null,
      );
    } catch {
      // ignore transient poll errors
      if (get().run.status === 'running' && get().run.runId === runId) {
        setTimeout(() => void poll(), 2000);
      }
    }
  };
  void poll();
}

const initialState = {
  bottomPanelOpen: false,
  bottomPanelTab: 'scenario' as const,
  activeScenarioId: null,
  liveOverrides: { global_rate_multiplier: 1.0 } as ScenarioOverrides,
  gateOverrides: {} as Record<string, GateOverrideEntry>,
  scenarios: defaultScenarios(),
  run: initialRun,
  simulationDurationMode: 'until_stopped' as SimulationDurationMode,
  simulationRunMode: 'realtime' as SimulationRunMode,
  durationMinutes: 60,
  visualFps: DEFAULT_VISUAL_FPS,
  receivedVisualFps: 0,
  renderedVisualFps: 0,
  droppedSnapshotCount: 0,
  runPreferencesHydrated: false,
  lastSubmittedRunConfig: null,
  wsRef: null as WebSocket | null,
  showProductSprites: true,
  simulationTimeScale: 1,
  highlightedGateId: null as string | null,
};

function upsertInputOverride(
  overrides: ScenarioOverrides,
  nodeId: string,
  patch: Partial<ScenarioOverrides['input_overrides'] extends (infer U)[] | undefined ? U : never>,
): ScenarioOverrides {
  const list = [...(overrides.input_overrides ?? [])];
  const idx = list.findIndex((o) => o.node_id === nodeId);
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...patch };
  } else {
    list.push({ node_id: nodeId, ...patch });
  }
  return { ...overrides, input_overrides: list };
}

function upsertProcessorOverride(
  overrides: ScenarioOverrides,
  nodeId: string,
  capacity: number | undefined,
): ScenarioOverrides {
  const list = [...(overrides.processor_overrides ?? [])];
  const idx = list.findIndex((o) => o.node_id === nodeId);
  if (capacity === undefined) {
    return { ...overrides, processor_overrides: list.filter((o) => o.node_id !== nodeId) };
  }
  if (idx >= 0) {
    list[idx] = { ...list[idx], capacity };
  } else {
    list.push({ node_id: nodeId, capacity });
  }
  return { ...overrides, processor_overrides: list };
}

function simulationErrorFromCaught(err: unknown): {
  error: string;
  issues: ValidationIssue[];
} {
  if (err instanceof Error) {
    const withIssues = err as Error & {
      issues?: ValidationIssue[];
      validation?: { issues?: ValidationIssue[] };
    };
    const issues =
      withIssues.issues ?? withIssues.validation?.issues ?? [];
    return { error: err.message, issues };
  }
  return { error: 'Simulation failed', issues: [] };
}

function closeWs(ws: WebSocket | null) {
  if (ws && ws.readyState <= WebSocket.OPEN) {
    ws.close();
  }
}

export const useSimulationStore = create<SimulationState>((set, get) => ({
  ...initialState,

  setBottomPanelOpen: (bottomPanelOpen) => set({ bottomPanelOpen }),
  setBottomPanelTab: (bottomPanelTab) => set({ bottomPanelTab, bottomPanelOpen: true }),
  setShowProductSprites: (showProductSprites) => set({ showProductSprites }),

  setScenarios: (scenarios) =>
    set({
      scenarios: scenarios.length > 0 ? scenarios : defaultScenarios(),
    }),

  setActiveScenarioId: (activeScenarioId) => set({ activeScenarioId }),

  setGlobalRateMultiplier: (value) =>
    set((s) => ({
      liveOverrides: {
        ...s.liveOverrides,
        global_rate_multiplier: value,
      },
    })),

  setInputRateMultiplier: (nodeId, value) =>
    set((s) => ({
      liveOverrides: upsertInputOverride(s.liveOverrides, nodeId, {
        rate_multiplier: value,
      }),
    })),

  setProcessorCapacity: (nodeId, capacity) =>
    set((s) => ({
      liveOverrides: upsertProcessorOverride(s.liveOverrides, nodeId, capacity),
    })),

  setBellCurveAdjustments: (nodeId, patch) =>
    set((s) => {
      const existing = s.liveOverrides.input_overrides?.find(
        (o) => o.node_id === nodeId,
      );
      const bell = { ...(existing?.bell_curve ?? {}), ...patch };
      return {
        liveOverrides: upsertInputOverride(s.liveOverrides, nodeId, {
          bell_curve: bell,
        }),
      };
    }),

  toggleGate: (nodeId, state) => {
    const { run, wsRef } = get();
    const canDeliver = isGateToggleDeliverable(
      run.status,
      run.runId,
      wsRef?.readyState,
    );
    const commandId = canDeliver ? crypto.randomUUID() : undefined;
    const uiState = canDeliver
      ? transitionalGateUiState(state)
      : stableGateUiState(state);

    if (canDeliver && commandId) {
      wsRef!.send(
        JSON.stringify({
          type: 'gate_override',
          command_id: commandId,
          gate_id: nodeId,
          state,
        }),
      );
      scheduleGatePendingTimeout(nodeId, set);
    } else {
      clearGatePendingTimeout(nodeId);
    }
    set((s) => {
      const gate_states = [...(s.liveOverrides.gate_states ?? [])];
      const idx = gate_states.findIndex((g) => g.node_id === nodeId);
      if (idx >= 0) {
        gate_states[idx] = { node_id: nodeId, state };
      } else {
        gate_states.push({ node_id: nodeId, state });
      }
      const liveGateStates = { ...s.run.liveGateStates, [nodeId]: state };
      return {
        liveOverrides: { ...s.liveOverrides, gate_states },
        gateOverrides: {
          ...s.gateOverrides,
          [nodeId]: {
            node_id: nodeId,
            state,
            changed_at: new Date().toISOString(),
            source: 'manual',
            ui_state: uiState,
            pending_command_id: commandId,
          },
        },
        run: { ...s.run, liveGateStates },
      };
    });
  },

  saveScenarioPreset: (preset) =>
    set((s) => {
      const idx = s.scenarios.findIndex((p) => p.id === preset.id);
      const scenarios =
        idx >= 0
          ? s.scenarios.map((p, i) => (i === idx ? preset : p))
          : [...s.scenarios, preset];
      return { scenarios, activeScenarioId: preset.id };
    }),

  deleteScenarioPreset: (id) =>
    set((s) => ({
      scenarios: s.scenarios.filter((p) => p.id !== id),
      activeScenarioId: s.activeScenarioId === id ? null : s.activeScenarioId,
    })),

  loadScenarioPreset: (preset) =>
    set({
      activeScenarioId: preset.id,
      liveOverrides: {
        global_rate_multiplier: preset.overrides.global_rate_multiplier ?? 1.0,
        input_overrides: preset.overrides.input_overrides ?? [],
        gate_states: preset.overrides.gate_states ?? [],
        processor_overrides: preset.overrides.processor_overrides ?? [],
      },
      gateOverrides: Object.fromEntries(
        (preset.overrides.gate_states ?? []).map((g) => [
          g.node_id,
          {
            node_id: g.node_id,
            state: g.state,
            changed_at: new Date().toISOString(),
            source: 'scenario' as const,
          },
        ]),
      ),
    }),

  setSimulationDurationMode: (simulationDurationMode) => set({ simulationDurationMode }),

  setSimulationRunMode: (simulationRunMode) => set({ simulationRunMode }),

  setDurationMinutes: (durationMinutes) =>
    set({
      durationMinutes: Math.min(
        MAX_DURATION_MINUTES,
        Math.max(1, Math.round(durationMinutes)),
      ),
    }),

  setVisualFps: (visualFps) =>
    set({
      visualFps: Math.min(
        MAX_VISUAL_FPS,
        Math.max(MIN_VISUAL_FPS, Math.round(visualFps)),
      ),
    }),

  setSimulationTimeScale: (scale) => {
    const allowed = [1, 2, 5];
    const clamped = allowed.includes(scale) ? scale : 1;
    const { run, wsRef } = get();
    if (run.status === 'running' && wsRef?.readyState === WebSocket.OPEN) {
      wsRef.send(
        JSON.stringify({
          type: 'set_time_scale',
          time_scale: clamped,
        }),
      );
    }
    set({ simulationTimeScale: clamped });
  },

  setHighlightedGateId: (highlightedGateId) => set({ highlightedGateId }),

  hydrateRunPreferences: async (projectId) => {
    let preferences = loadLocalRunPreferences();
    const localConfig = resolveProjectRunConfig(preferences, projectId);
    get().applyProjectRunConfig(localConfig);
    set({ runPreferencesHydrated: true });

    try {
      const { preferences: remoteRaw } = await fetchUserPreferences();
      const remotePrefs = parseSimulationRunPreferences(
        remoteRaw.simulation_run_preferences,
      );
      if (remotePrefs) {
        preferences = remotePrefs;
        saveLocalRunPreferences(preferences);
        get().applyProjectRunConfig(resolveProjectRunConfig(preferences, projectId));
      }
    } catch {
      /* offline or not logged in */
    }
    set({ runPreferencesHydrated: true });
  },

  applyProjectRunConfig: (config) => {
    const scenarios = get().scenarios;
    const scenarioStillExists =
      !config.active_scenario_id ||
      scenarios.some((scenario) => scenario.id === config.active_scenario_id);
    set({
      simulationRunMode: config.run_mode,
      simulationDurationMode: config.duration_mode,
      durationMinutes: config.duration_minutes,
      visualFps: config.visual_fps,
      showProductSprites: config.show_products,
      activeScenarioId: scenarioStillExists
        ? (config.active_scenario_id ?? null)
        : get().activeScenarioId,
    });
  },

  persistRunPreferences: async (projectId, config) => {
    const preferences = mergeProjectRunConfig(
      loadLocalRunPreferences(),
      projectId,
      config,
    );
    saveLocalRunPreferences(preferences);
    set({ lastSubmittedRunConfig: config });
    try {
      await updateUserPreferences({
        simulation_run_preferences: preferences,
      });
    } catch {
      /* keep local copy */
    }
  },

  persistScenarios: async (projectId) => {
    const { scenarios } = get();
    await updateScenarios(projectId, scenarios);
  },

  runSimulation: async (projectId) => {
    const {
      simulationDurationMode,
      simulationRunMode,
      durationMinutes,
      visualFps,
      activeScenarioId,
      liveOverrides,
      wsRef,
      simulationTimeScale,
    } = get();
    const runUntilStopped = simulationDurationMode === 'until_stopped';
    const isRealtime = simulationRunMode === 'realtime';
    const submittedConfig: ProjectRunConfig = {
      run_mode: simulationRunMode,
      duration_mode: simulationDurationMode,
      duration_minutes: durationMinutes,
      visual_fps: visualFps,
      show_products: get().showProductSprites,
      active_scenario_id: activeScenarioId,
    };
    void get().persistRunPreferences(projectId, submittedConfig);
    activeSimulationRunMode = simulationRunMode;
    closeWs(wsRef);
    stopVisualDrain();
    const document = useEditorStore.getState().document;
    if (document) {
      mergeVisualCatalog(collectVisualCatalogFromDocument(document));
    }

    const durationSeconds = durationMinutes * 60;
    const useAsync =
      isRealtime || runUntilStopped || durationSeconds > 300;

    set({
      run: {
        ...initialRun,
        status: 'running',
        progressPct: 0,
        runUntilStopped,
        runMode: simulationRunMode,
      },
      bottomPanelOpen: true,
      bottomPanelTab: isRealtime ? 'gates' : 'results',
      wsRef: null,
    });

    try {
      const response = await createSimulationRun(projectId, {
        ...(runUntilStopped
          ? { run_until_stopped: true }
          : { duration_seconds: durationSeconds }),
        scenario_id: activeScenarioId ?? undefined,
        scenario_overrides: liveOverrides,
        async_mode: useAsync,
        ...(isRealtime
          ? {
              realtime: true,
              visual_fps: visualFps,
              time_scale: simulationTimeScale,
            }
          : {}),
      });

      if (response.status === 'completed') {
        const results = await getSimulationResults(response.id);
        applyRunFinished(
          set,
          response.id,
          'completed',
          results,
          runUntilStopped,
        );
        return;
      }

      set({
        run: {
          runId: response.id,
          status: 'running',
          error: null,
          issues: [],
          summary: null,
          timeseries: [],
          progressPct: response.progress_pct ?? 0,
          liveGateStates: {},
          liveOverlay: null,
          liveSimTime: 0,
          visualPlaybackActive: false,
          runUntilStopped,
          runMode: simulationRunMode,
          wsConnectionState: 'connecting',
          visualizedProductCount: null,
          totalProductCount: null,
        },
      });

      if (!isRealtime) {
        pollCalculateRun(response.id, runUntilStopped, set, get);
        return;
      }

      const connectWs = (attempt = 0): Promise<WebSocket> =>
        new Promise((resolve, reject) => {
          set({
            run: {
              ...get().run,
              wsConnectionState: attempt > 0 ? 'reconnecting' : 'connecting',
            },
          });
          const ws = new WebSocket(simulationStreamUrl(response.id));
          set({ wsRef: ws });

          const fail = () => {
            if (attempt < 4 && get().run.status === 'running') {
              setTimeout(() => {
                void connectWs(attempt + 1)
                  .then(resolve)
                  .catch(reject);
              }, Math.min(1000 * 2 ** attempt, 8000));
            } else {
              reject(new Error('WebSocket connection failed'));
            }
          };

          ws.onopen = () => {
            set((s) => ({
              run: { ...s.run, wsConnectionState: 'connected' },
            }));
            resolve(ws);
          };
          ws.onerror = fail;

          ws.onmessage = (event) => {
            let msg: StreamMessage;
            try {
              msg = JSON.parse(event.data as string) as StreamMessage;
            } catch {
              return;
            }

            if (msg.type === 'status') {
              set((s) => ({
                run: { ...s.run, progressPct: msg.progress_pct },
              }));
              const { run } = get();
              maybePollRunCompletionAt100(
                run.runId,
                msg.progress_pct,
                run.runUntilStopped,
                run.status,
                set,
                get,
              );
            }

            if (msg.type === 'visual_catalog') {
              setVisualCatalog(msg.styles);
            }

            if (msg.type === 'live_frame') {
              enqueueLiveFrame(msg, visualFps, set, get);
            }

            if (msg.type === 'gate_ack') {
              clearGatePendingTimeout(msg.gate_id);
              set((s) => {
                const gateOverrides = { ...s.gateOverrides };
                const entry = gateOverrides[msg.gate_id];
                if (!entry) return {};
                if (
                  entry.pending_command_id &&
                  entry.pending_command_id !== msg.command_id
                ) {
                  return {};
                }
                if (!msg.applied) {
                  gateOverrides[msg.gate_id] = {
                    ...entry,
                    ui_state: 'ERROR',
                    pending_command_id: undefined,
                  };
                  return { gateOverrides };
                }
                const nextState = msg.state as 'open' | 'closed';
                gateOverrides[msg.gate_id] = {
                  ...entry,
                  state: nextState,
                  changed_at: new Date().toISOString(),
                  ui_state: stableGateUiState(nextState),
                  pending_command_id: undefined,
                };
                return {
                  gateOverrides,
                  run: {
                    ...s.run,
                    liveGateStates: {
                      ...s.run.liveGateStates,
                      [msg.gate_id]: nextState,
                    },
                  },
                };
              });
            }

            if (msg.type === 'replay') {
              const latest = msg.steps[msg.steps.length - 1];
              if (latest) {
                set((s) => ({
                  run: {
                    ...s.run,
                    progressPct: msg.progress_pct,
                    liveSimTime: latest.sim_time,
                  },
                }));
                if (latest.live_frame && activeSimulationRunMode === 'realtime') {
                  enqueueLiveFrame(
                    {
                      type: 'live_frame',
                      frame: latest.live_frame,
                      progress_pct: msg.progress_pct,
                      gate_states: latest.gate_states,
                    },
                    visualFps,
                    set,
                    get,
                  );
                }
              }
            }

            if (msg.type === 'step') {
              if (activeSimulationRunMode === 'realtime') {
                set((s) => ({
                  run: {
                    ...s.run,
                    ...applyStreamStepLive(s.run, msg),
                  },
                }));
              }
            }

            if (msg.type === 'completed') {
              closeWs(ws);
              finishRunFromStreamMessage(
                response.id,
                'completed',
                msg.summary,
                msg.progress_pct,
                runUntilStopped,
                set,
                get,
              );
            }

            if (msg.type === 'cancelled') {
              closeWs(ws);
              finishRunFromStreamMessage(
                response.id,
                'cancelled',
                msg.summary,
                msg.progress_pct,
                runUntilStopped,
                set,
                get,
              );
            }

            if (msg.type === 'error') {
              closeWs(ws);
              clearAllGatePendingTimeouts();
              set((s) => ({
                wsRef: null,
                gateOverrides: finalizeGateOverrides(s.gateOverrides),
                run: {
                  ...initialRun,
                  runId: response.id,
                  status: 'error',
                  error: msg.message,
                  issues: [],
                  progressPct: 0,
                  liveGateStates: {},
                  liveOverlay: null,
                  runMode: simulationRunMode,
                },
              }));
            }
          };

          ws.onclose = () => {
            const { run } = get();
            if (run.status === 'running' && run.runId === response.id) {
              if (!runUntilStopped && run.progressPct >= 100) {
                set((s) => ({
                  run: { ...s.run, wsConnectionState: 'disconnected' },
                }));
                pollRunCompletion(response.id, runUntilStopped, set, get);
                return;
              }
              set((s) => ({
                run: { ...s.run, wsConnectionState: 'reconnecting' },
              }));
              void (async () => {
                try {
                  const status = await getSimulationStatus(response.id);
                  if (status.finished) {
                    const results = await getSimulationResults(response.id);
                    applyRunFinished(
                      set,
                      response.id,
                      status.status === 'cancelled' ? 'cancelled' : 'completed',
                      results,
                      runUntilStopped,
                      status.progress_pct,
                    );
                  } else if (attempt < 4) {
                    void connectWs(attempt + 1);
                  }
                } catch {
                  // ignore poll errors on close
                }
              })();
            }
          };
        });

      try {
        await connectWs();
      } catch {
        pollCalculateRun(response.id, runUntilStopped, set, get);
      }
    } catch (err) {
      const { error, issues } = simulationErrorFromCaught(err);
      set({
        run: {
          ...initialRun,
          status: 'error',
          error,
          issues,
          runMode: simulationRunMode,
        },
      });
    }
  },

  cancelSimulation: async () => {
    const { run, wsRef } = get();
    if (!run.runId || run.status !== 'running') return;

    const runId = run.runId;
    stopVisualDrain();
    closeWs(wsRef);

    set({
      wsRef: null,
      run: {
        ...run,
        visualPlaybackActive: false,
        error: null,
      },
    });

    try {
      await cancelSimulationRun(runId);

      let status = await getSimulationStatus(runId);
      for (let attempt = 0; attempt < 40 && !status.finished; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        status = await getSimulationStatus(runId);
      }

      const results = await getSimulationResults(runId);
      set({
        run: {
          ...get().run,
          runId,
          status: 'cancelled',
          summary: results.summary as Record<string, unknown>,
          timeseries: results.timeseries as SimulationRunState['timeseries'],
          progressPct: status.progress_pct,
          visualPlaybackActive: false,
          liveOverlay: null,
        },
      });
    } catch (err) {
      try {
        const status = await getSimulationStatus(runId);
        if (status.finished) {
          const results = await getSimulationResults(runId);
          set({
            run: {
              ...get().run,
              runId,
              status:
                status.status === 'cancelled'
                  ? 'cancelled'
                  : status.status === 'failed'
                    ? 'error'
                    : 'completed',
              summary: results.summary as Record<string, unknown>,
              timeseries: results.timeseries as SimulationRunState['timeseries'],
              progressPct: status.progress_pct,
              visualPlaybackActive: false,
              liveOverlay: null,
              error:
                status.status === 'failed'
                  ? 'Simulation failed'
                  : null,
            },
          });
          return;
        }
      } catch {
        // fall through to error message
      }
      set((s) => ({
        run: {
          ...s.run,
          error: err instanceof Error ? err.message : 'Stop failed',
        },
      }));
    }
  },

  resetRun: () => {
    const { wsRef } = get();
    closeWs(wsRef);
    stopVisualDrain();
    clearAllGatePendingTimeouts();
    set((s) => ({
      run: initialRun,
      wsRef: null,
      gateOverrides: finalizeGateOverrides(s.gateOverrides),
    }));
  },

  setRunValidationIssues: (issues, error = null) =>
    set((state) => ({
      run: {
        ...state.run,
        status: issues.length > 0 ? 'error' : 'idle',
        issues,
        error:
          error ??
          (issues.length > 0 ? 'Project validation failed' : null),
        summary: issues.length > 0 ? state.run.summary : null,
        timeseries: issues.length > 0 ? state.run.timeseries : [],
        progressPct: issues.length > 0 ? state.run.progressPct : 0,
      },
    })),

  reset: () => {
    const { wsRef } = get();
    closeWs(wsRef);
    stopVisualDrain();
    clearAllGatePendingTimeouts();
    set({ ...initialState, wsRef: null });
  },
}));
